/** @type {import('../lib/dataTable').DataTableColumn[]} */
export const STACK_SAMPLE_APPROVAL_BASE_COLUMNS = [
  {
    id: "party_name",
    label: "Party",
    accessorKey: "party_name",
    filterType: "select",
    filterOptions: [{ value: "", label: "All parties" }],
    matchFilter: (row, value) => !value || row.party_name === value,
  },
  {
    id: "quality",
    label: "Item / Quality",
    accessorKey: "quality",
    filterType: "select",
    filterOptions: [{ value: "", label: "All items" }],
    matchFilter: (row, value) => !value || row.quality === value,
  },
  {
    id: "sample_code",
    label: "Sample ID",
    accessorKey: "sample_code",
    filterType: "text",
  },
  {
    id: "intake_code",
    label: "Intake",
    accessorKey: "intake_code",
    filterType: "text",
  },
  {
    id: "intake_timing",
    label: "Timing",
    accessorKey: "intake_timing",
    filterType: "select",
    filterOptions: [
      { value: "", label: "All" },
      { value: "BEFORE_CONTRACT", label: "Before contract" },
      { value: "AFTER_CONTRACT", label: "After contract" },
    ],
    matchFilter: (row, value) => !value || row.intake_timing === value,
  },
  {
    id: "lab_created_at",
    label: "Lab date",
    accessorKey: "lab_created_at",
    filterType: "date",
  },
  {
    id: "verdict",
    label: "Verdict",
    accessor: (row) => row.lab_report?.verdict ?? "",
    filterType: "select",
    filterOptions: [
      { value: "", label: "All" },
      { value: "AUTO_PASS", label: "Auto pass" },
      { value: "REVIEW", label: "Review" },
    ],
    matchFilter: (row, value) => !value || row.lab_report?.verdict === value,
  },
];

/**
 * Flatten pending-approval intakes into one row per stack line (for client filters).
 */
export function flattenApprovalQueue(queue) {
  const rows = [];
  for (const intake of queue || []) {
    for (const line of intake.stack_lines || []) {
      rows.push({
        ...line,
        intake_id: intake.id,
        intake_code: intake.code,
        party_id: intake.party_id,
        party_name: intake.party_name,
        intake_timing: intake.intake_timing,
        intake_status: intake.status,
        intake_created_at: intake.created_at,
        lab_created_at: line.lab_report?.created_at || intake.created_at,
        purchase_contracts: intake.purchase_contracts,
      });
    }
  }
  return rows;
}

/**
 * Merge dynamic party / quality options into column defs.
 */
export function buildApprovalColumns(flatRows) {
  const parties = [...new Set(flatRows.map((r) => r.party_name).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
  const qualities = [...new Set(flatRows.map((r) => r.quality).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );

  return STACK_SAMPLE_APPROVAL_BASE_COLUMNS.map((col) => {
    if (col.id === "party_name") {
      return {
        ...col,
        filterOptions: [
          { value: "", label: "All parties" },
          ...parties.map((name) => ({ value: name, label: name })),
        ],
      };
    }
    if (col.id === "quality") {
      return {
        ...col,
        filterOptions: [
          { value: "", label: "All items" },
          ...qualities.map((q) => ({ value: q, label: q })),
        ],
      };
    }
    return col;
  });
}

/**
 * Group filtered flat rows back by intake for card rendering.
 */
export function groupRowsByIntake(rows) {
  const map = new Map();
  for (const row of rows) {
    if (!map.has(row.intake_id)) {
      map.set(row.intake_id, {
        id: row.intake_id,
        code: row.intake_code,
        party_name: row.party_name,
        party_id: row.party_id,
        intake_timing: row.intake_timing,
        status: row.intake_status,
        created_at: row.intake_created_at,
        purchase_contracts: row.purchase_contracts,
        stack_lines: [],
      });
    }
    map.get(row.intake_id).stack_lines.push(row);
  }
  return [...map.values()];
}
